type NetworkInformation = {
  saveData?: boolean
  effectiveType?: string
}

const canPrefetch = () => {
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection

  if (connection?.saveData) return false

  return !connection?.effectiveType || connection.effectiveType === '4g'
}

const scheduleIdle = (fn: () => void) => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => fn())
  } else {
    setTimeout(fn, 0)
  }
}

/**
 * Inserts a <link rel="prefetch"> for a document/URL once.
 * Skips when Save-Data is on or connection is slower than 4g.
 */
export const prefetchLink = (href: string) => {
  if (!href || !canPrefetch()) return
  if (document.querySelector(`link[rel="prefetch"][href="${href}"]`)) return

  const link = document.createElement('link')

  link.rel = 'prefetch'
  link.href = href
  document.head.appendChild(link)
}

/** Prefetch during browser idle time (good for batches of sibling links). */
export const schedulePrefetch = (href: string) => {
  if (!href || !canPrefetch()) return

  scheduleIdle(() => prefetchLink(href))
}

/**
 * Prefetch when an element enters (near) the viewport.
 * Used by PLP cards — fires once, then disconnects.
 */
export const attachInViewPrefetch = (node: Element | null, href: string) => {
  if (!node || !href) return

  let done = false
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (done || !entry.isIntersecting || !canPrefetch()) return

        scheduleIdle(() => prefetchLink(href))
        done = true
        io.disconnect()
      })
    },
    { rootMargin: '400px' }
  )

  io.observe(node)

  return () => io.disconnect()
}

/**
 * Prefetch on first hover / keyboard focus.
 * Useful for flavour pills — matches Shopify's moderate speculation rules.
 */
export const attachHoverPrefetch = (node: Element | null, href: string) => {
  if (!node || !href) return

  let done = false
  const run = () => {
    if (done) return

    done = true
    prefetchLink(href)
    node.removeEventListener('pointerenter', run)
    node.removeEventListener('focus', run)
  }

  node.addEventListener('pointerenter', run, { passive: true })
  node.addEventListener('focus', run)

  return () => {
    node.removeEventListener('pointerenter', run)
    node.removeEventListener('focus', run)
  }
}
